import { quantile } from "@/utils/statistics";
import { CellHeights, Grid } from "./height-grid";

/** Headroom below an upper level, lower surfaces are furniture or equipment tops */
const MIN_CLEARANCE = 2;
/** An upper level is under a ceiling or a roof this high above it, others are roofs */
const MIN_HEADROOM = 1.8;
/** Ceilings are sampled more sparsely than floors, as they are further from the scanners */
const CEILING_REACH = 0.6;
/** Resolution of the histogram of the upper level heights, in meters */
const LEVEL_BIN = 0.05;
/** A level height is the highest one of the histogram within this distance */
const LEVEL_PEAK_RADIUS = 0.2;
/** An upper level covers at least this share of the lowest level cells */
const MIN_LEVEL_SHARE = 0.005;
/** Level heights closer than this are the floor and ceiling of the same slab, or a split level */
const LEVEL_MERGE_DISTANCE = 1;
/** Cells of a level are within this distance of its heights */
const LEVEL_TOLERANCE = 0.15;
/** The top and the underside of a slab, seen from both levels, are this far apart */
const MIN_SLAB_THICKNESS = 0.1;
const MAX_SLAB_THICKNESS = 0.7;
/** Share of an upper level where both sides of its slab are scanned; suspended ceilings and roofs are seen from one side */
const MIN_SLAB_SHARE = 0.3;
/** Far above the level under it, the underside of a slab is too sparse to be found */
const MIN_CLEARANCE_WITHOUT_SLAB = 6;

/** A horizontal surface that may be part of an upper level */
type Candidate = {
  cell: number;
  height: number;
  /** Height above the lowest surface of the cell */
  clearance: number;
  /** True if the cell has another horizontal surface a slab thickness away */
  isSlab: boolean;
};

/** An upper level, with its height in each cell, NaN outside */
export type UpperLevel = { elevation: number; heights: Float32Array };

/**
 * @param values One value per cell, NaN where unknown
 * @param grid The grid
 * @param radius Radius of the square neighborhood, in cells
 * @returns the highest value of each neighborhood
 */
function maxFilter(
  values: Float32Array,
  { width, height }: Grid,
  radius: number,
): Float32Array {
  const rows = new Float32Array(values.length).fill(-Infinity);
  const out = new Float32Array(values.length).fill(-Infinity);
  for (let row = 0; row < height; row++) {
    for (let column = 0; column < width; column++) {
      for (let d = -radius; d <= radius; d++) {
        const c = column + d;
        const value = values[row * width + c];
        if (c >= 0 && c < width && value > rows[row * width + column]) {
          rows[row * width + column] = value;
        }
      }
    }
  }
  for (let row = 0; row < height; row++) {
    for (let column = 0; column < width; column++) {
      for (let d = -radius; d <= radius; d++) {
        const r = row + d;
        const value = rows[r * width + column];
        if (r >= 0 && r < height && value > out[row * width + column]) {
          out[row * width + column] = value;
        }
      }
    }
  }
  return out;
}

/**
 * @param grid The grid
 * @param cells The point heights by cell
 * @param layers The candidate surfaces of each cell
 * @param isHorizontal Which layers are horizontal surfaces
 * @param lowest Height of the lowest surface of each cell
 * @returns the horizontal surfaces with headroom below and a ceiling above
 */
function findCandidates(
  grid: Grid,
  cells: CellHeights,
  { starts, heights }: CellHeights,
  isHorizontal: Uint8Array,
  lowest: Float32Array,
): Candidate[] {
  const tops = new Float32Array(lowest.length).fill(NaN);
  for (let cell = 0; cell < tops.length; cell++) {
    if (cells.starts[cell + 1] > cells.starts[cell]) {
      tops[cell] = cells.heights[cells.starts[cell + 1] - 1];
    }
  }
  const nearTops = maxFilter(
    tops,
    grid,
    Math.max(1, Math.round(CEILING_REACH / grid.cellSize)),
  );

  const candidates: Candidate[] = [];
  for (let cell = 0; cell < lowest.length; cell++) {
    if (Number.isNaN(lowest[cell])) continue;
    for (let k = starts[cell]; k < starts[cell + 1]; k++) {
      const level = heights[k];
      if (
        !isHorizontal[k] ||
        level - lowest[cell] < MIN_CLEARANCE ||
        nearTops[cell] < level + MIN_HEADROOM
      ) {
        continue;
      }
      let isSlab = false;
      for (let other = starts[cell]; other < starts[cell + 1]; other++) {
        const thickness = Math.abs(heights[other] - level);
        isSlab ||=
          !!isHorizontal[other] &&
          thickness >= MIN_SLAB_THICKNESS &&
          thickness <= MAX_SLAB_THICKNESS;
      }
      candidates.push({
        cell,
        height: level,
        clearance: level - lowest[cell],
        isSlab,
      });
    }
  }
  return candidates;
}

/**
 * @param candidates The candidate surfaces
 * @param lowestCells Number of cells of the lowest level
 * @returns the height ranges of the levels: the peaks of the histogram of the candidate heights, merged per slab
 */
function groupHeights(
  candidates: Candidate[],
  lowestCells: number,
): Array<{ low: number; high: number }> {
  let min = Infinity;
  let max = -Infinity;
  for (const { height } of candidates) {
    min = Math.min(min, height);
    max = Math.max(max, height);
  }
  const bins = new Float64Array(Math.floor((max - min) / LEVEL_BIN) + 1);
  for (const { height } of candidates) {
    bins[Math.floor((height - min) / LEVEL_BIN)]++;
  }
  const radius = Math.round(LEVEL_PEAK_RADIUS / LEVEL_BIN);
  const groups: Array<{ low: number; high: number }> = [];
  bins.forEach((count, i) => {
    if (count < lowestCells * MIN_LEVEL_SHARE) return;
    for (let j = i - radius; j <= i + radius; j++) {
      if (j !== i && (bins[j] ?? 0) > count) return;
    }
    const peak = min + (i + 0.5) * LEVEL_BIN;
    const last = groups.at(-1);
    if (last && peak - last.high <= LEVEL_MERGE_DISTANCE) last.high = peak;
    else groups.push({ low: peak, high: peak });
  });
  return groups;
}

/**
 * Finds the upper levels: horizontal surfaces with headroom below and a ceiling above, on slabs scanned from both sides,
 * so furniture, equipment tops, suspended ceilings and roofs are left out
 *
 * @param grid The grid
 * @param cells The point heights by cell
 * @param layers The candidate surfaces of each cell
 * @param isHorizontal Which layers are horizontal surfaces
 * @param lowest Height of the lowest surface of each cell
 * @returns the upper levels, from the lowest
 */
export function findUpperLevels(
  grid: Grid,
  cells: CellHeights,
  layers: CellHeights,
  isHorizontal: Uint8Array,
  lowest: Float32Array,
): UpperLevel[] {
  const candidates = findCandidates(grid, cells, layers, isHorizontal, lowest);
  if (!candidates.length) return [];
  const lowestCells = lowest.filter((value) => !Number.isNaN(value)).length;

  return groupHeights(candidates, lowestCells).flatMap(({ low, high }) => {
    const members = candidates.filter(
      ({ height }) =>
        height >= low - LEVEL_TOLERANCE && height <= high + LEVEL_TOLERANCE,
    );
    const slabShare =
      members.filter(({ isSlab }) => isSlab).length / members.length;
    const clearance = quantile(
      members.map((member) => member.clearance).sort((a, b) => a - b),
      0.5,
    );
    if (slabShare < MIN_SLAB_SHARE && clearance < MIN_CLEARANCE_WITHOUT_SLAB) {
      return [];
    }
    const heights = new Float32Array(lowest.length).fill(NaN);
    for (const member of members) heights[member.cell] = member.height;
    return { elevation: low, heights };
  });
}
